// Custom collection filters' interactive controls: dual-handle range slider (ABV), Age
// Statement checkbox bands, price radio bands, and the distillery search/show-more list.
//
// Two modes, set via [data-slider-mode]:
// - "value": the two <input type="range"> handles carry the real filter.v.* name attribute
//   directly, so dragging them fires native input events that bubble to facets.js's existing
//   form-level listener — no extra submission logic needed here, only visual sync (fill bar,
//   clamping, readout).
// - "checkbox": used for filters that are really a list of discrete values (Age, ABV) rather
//   than a true Shopify price_range. The slider handles carry no name themselves; on release,
//   this checks/unchecks the underlying (visually hidden) checkboxes whose value falls inside
//   the selected range, then fires an input event on one of them so facets.js picks it up
//   exactly as if the user had clicked checkboxes directly.
(function () {
  function clampHandles(minInput, maxInput) {
    var minVal = parseFloat(minInput.value);
    var maxVal = parseFloat(maxInput.value);
    if (minVal > maxVal) {
      if (document.activeElement === minInput) {
        maxInput.value = minVal;
      } else {
        minInput.value = maxVal;
      }
    }
  }

  function updateFill(slider) {
    var minInput = slider.querySelector('[data-slider-min]');
    var maxInput = slider.querySelector('[data-slider-max]');
    var fill = slider.querySelector('[data-slider-fill]');
    if (!minInput || !maxInput || !fill) return;

    var min = parseFloat(minInput.min);
    var max = parseFloat(minInput.max);
    var minVal = parseFloat(minInput.value);
    var maxVal = parseFloat(maxInput.value);
    var range = max - min || 1;
    var left = ((minVal - min) / range) * 100;
    var right = ((maxVal - min) / range) * 100;
    fill.style.left = left + '%';
    fill.style.width = Math.max(right - left, 0) + '%';

    var readoutMin = slider.querySelector('[data-slider-readout-min]');
    var readoutMax = slider.querySelector('[data-slider-readout-max]');
    var prefix = minInput.dataset.prefix || '';
    if (readoutMin) readoutMin.textContent = prefix + minVal;
    if (readoutMax) readoutMax.textContent = prefix + maxVal;
  }

  function applyCheckboxMode(slider) {
    var minInput = slider.querySelector('[data-slider-min]');
    var maxInput = slider.querySelector('[data-slider-max]');
    var minVal = parseFloat(minInput.value);
    var maxVal = parseFloat(maxInput.value);
    var container = document.getElementById(slider.dataset.checkboxTarget);
    if (!container) return null;

    var checkboxes = container.querySelectorAll('[data-slider-value]');
    checkboxes.forEach(function (checkbox) {
      var val = parseFloat(checkbox.dataset.sliderValue);
      var shouldCheck = val >= minVal && val <= maxVal;
      if (checkbox.checked !== shouldCheck) {
        checkbox.checked = shouldCheck;
      }
    });
    return checkboxes[0] || null;
  }

  // mode "value" (the price slider) only: a handle's real filter.v.price.gte/.lte "name" is
  // removed whenever it sits at its own untouched default (min handle at its floor, max handle
  // at its ceiling) and restored once dragged away from it. Without this, FormData(form) (see
  // facets.js's debounced form-level submit listener) always includes both handles' current
  // values on ANY filter change - even unrelated ones, like checking a distillery checkbox -
  // which made Shopify report an "active" £0-£max price filter that the shopper never touched.
  function syncPriceInputName(input, boundaryValue) {
    if (typeof input.dataset.paramName === 'undefined') {
      input.dataset.paramName = input.getAttribute('name') || '';
    }
    var paramName = input.dataset.paramName;
    if (!paramName) return;
    var atDefault = parseFloat(input.value) === parseFloat(boundaryValue);
    if (atDefault) {
      input.removeAttribute('name');
    } else if (input.getAttribute('name') !== paramName) {
      input.setAttribute('name', paramName);
    }
  }

  function initSlider(slider) {
    if (slider.dataset.sliderInitialized) return;
    slider.dataset.sliderInitialized = 'true';

    var minInput = slider.querySelector('[data-slider-min]');
    var maxInput = slider.querySelector('[data-slider-max]');
    if (!minInput || !maxInput) return;
    var mode = slider.dataset.sliderMode;

    function syncPriceNames() {
      syncPriceInputName(minInput, minInput.min);
      syncPriceInputName(maxInput, maxInput.max);
    }

    function handleMove() {
      clampHandles(minInput, maxInput);
      updateFill(slider);
      if (mode === 'value') {
        syncPriceNames();
      }
    }

    function handleCommit() {
      if (mode === 'checkbox') {
        var firstCheckbox = applyCheckboxMode(slider);
        if (firstCheckbox) {
          firstCheckbox.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }
      // mode "value": the handles already carry real name attributes (see syncPriceNames, called
      // from handleMove on every 'input') and dragging's own 'input' events already reached
      // facets.js's debounced form listener - nothing further to trigger on commit. The price
      // pill's × button ("reset-slider" in assets/whisky-active-pills.js) fires its own 'input'
      // directly for the same reason, rather than relying on this handler to also fire one -
      // doing it from both places at once caused two overlapping submissions to race, with the
      // slightly-earlier one occasionally resolving last and clobbering the correct result.
    }

    minInput.addEventListener('input', handleMove);
    maxInput.addEventListener('input', handleMove);
    minInput.addEventListener('change', handleCommit);
    maxInput.addEventListener('change', handleCommit);

    if (mode === 'value') {
      syncPriceNames();
    }
    updateFill(slider);
  }

  // Price bands: a set of radio buttons that each represent a fixed gte/lte pair. They
  // deliberately carry no shared "name" attribute (which would make the browser group them,
  // but also means Shopify's form serialization picks up a stray "on" value for whichever one
  // is checked) — mutual exclusivity is instead enforced here in JS. Selecting one writes the
  // band's values into two real hidden inputs carrying the actual filter.v.price.gte/.lte
  // names, then fires an input event on them so facets.js's existing form-level listener
  // picks it up normally.
  function initPriceBands(container) {
    if (container.dataset.priceBandsInitialized) return;
    container.dataset.priceBandsInitialized = 'true';

    var radios = container.querySelectorAll('[data-price-band]');
    var gteInput = container.querySelector('[data-price-gte-input]');
    var lteInput = container.querySelector('[data-price-lte-input]');
    if (!radios.length || !gteInput || !lteInput) return;

    radios.forEach(function (radio) {
      radio.addEventListener('change', function () {
        radios.forEach(function (other) {
          if (other !== radio) other.checked = false;
        });
        gteInput.value = radio.dataset.gte || '';
        lteInput.value = radio.dataset.lte || '';
        gteInput.dispatchEvent(new Event('input', { bubbles: true }));
      });
    });
  }

  // Age Statement bands: checkboxes (any number can be checked at once, unlike the mutually
  // exclusive price bands) that each represent a fixed gt/lte range over the real discrete age
  // values. On any change, unions the ranges of every currently-checked band and sets the real
  // hidden checkboxes (same [data-slider-value] contract the range slider uses) to match, then
  // fires an input event so facets.js's form-level listener picks it up normally.
  function initAgeBands(container) {
    if (container.dataset.ageBandsInitialized) return;
    container.dataset.ageBandsInitialized = 'true';

    var bands = container.querySelectorAll('[data-age-band]');
    var target = document.getElementById(container.dataset.checkboxTarget);
    if (!bands.length || !target) return;

    function apply() {
      var ranges = [];
      bands.forEach(function (band) {
        if (band.checked) {
          ranges.push({
            gt: band.dataset.gt === '' ? -Infinity : parseFloat(band.dataset.gt),
            lte: band.dataset.lte === '' ? Infinity : parseFloat(band.dataset.lte)
          });
        }
      });

      var valueCheckboxes = target.querySelectorAll('[data-slider-value]');
      var firstChanged = null;
      valueCheckboxes.forEach(function (checkbox) {
        var val = parseFloat(checkbox.dataset.sliderValue);
        var shouldCheck = ranges.some(function (range) {
          return val > range.gt && val <= range.lte;
        });
        if (checkbox.checked !== shouldCheck) {
          checkbox.checked = shouldCheck;
          if (!firstChanged) firstChanged = checkbox;
        }
      });
      if (firstChanged) {
        firstChanged.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }

    bands.forEach(function (band) {
      band.addEventListener('change', apply);
    });
  }

  // Distillery filter's own list controls: a search-within-list input that narrows the visible
  // distillery names as you type (not product search — the header search bar handles that), and
  // a 5-item cap with a button that toggles between "+ Show more" (reveal the rest) and
  // "Show less" (collapse back to 5). Both gate the same items' [hidden] state, so they're wired
  // together to avoid the two mechanisms fighting each other.
  function initDistilleryList(container) {
    if (container.dataset.distilleryInitialized) return;
    container.dataset.distilleryInitialized = 'true';

    var input = container.querySelector('[data-filter-search-input]');
    var list = container.querySelector('[data-filter-searchable-list]');
    var showMoreButton = container.querySelector('[data-show-more-button]');
    if (!list) return;

    var limit = parseInt(container.dataset.showMoreLimit || '0', 10);
    var expanded = false;

    function updateVisibility() {
      var query = input ? input.value.trim().toLowerCase() : '';
      var items = list.querySelectorAll('[data-filter-search-text]');
      items.forEach(function (item, index) {
        var text = item.dataset.filterSearchText || '';
        var matchesSearch = query === '' || text.indexOf(query) !== -1;
        var withinLimit = !limit || expanded || query !== '' || index < limit;
        item.hidden = !matchesSearch || !withinLimit;
      });
    }

    if (input) {
      input.addEventListener('input', updateVisibility);
    }

    if (showMoreButton) {
      var showMoreText = showMoreButton.textContent;
      showMoreButton.addEventListener('click', function () {
        expanded = !expanded;
        showMoreButton.textContent = expanded ? 'Show less' : showMoreText;
        updateVisibility();
      });
    }

    updateVisibility();
  }

  function initAll(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-whisky-slider]').forEach(initSlider);
    scope.querySelectorAll('[data-whisky-price-bands]').forEach(initPriceBands);
    scope.querySelectorAll('[data-whisky-age-bands]').forEach(initAgeBands);
    scope.querySelectorAll('[data-distillery-controls]').forEach(initDistilleryList);
  }

  document.addEventListener('DOMContentLoaded', function () {
    initAll();

    // facets.js replaces #main-collection-filters' relevant contents wholesale on every filter
    // change (no custom event is dispatched), so newly-inserted controls need re-initializing.
    var filtersPanel = document.getElementById('main-collection-filters');
    if (filtersPanel) {
      new MutationObserver(function () {
        initAll(filtersPanel);
      }).observe(filtersPanel, { childList: true, subtree: true });
    }
  });
})();
